import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
} from "framer-motion";

const BOOK_URL =
  "https://static.prod-images.emergentagent.com/jobs/593645fc-6e4d-4e64-8f42-872062b68e36/images/7ebf9846ed45380259f52220d49891f01d37b908020a4898ab0a791cf7f01d8e.jpeg";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero({ scrollTo }) {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), {
    stiffness: 80,
    damping: 20,
  });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), {
    stiffness: 80,
    damping: 20,
  });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      onMouseMove={onMove}
      className="relative min-h-screen flex items-center grid-lines overflow-hidden pt-16"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 70% 55% at 72% 42%, rgba(255,85,0,0.09), transparent 60%)",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center py-24 w-full">
        <div>
          <MaskedLine
            delay={0.15}
            className="font-mono text-xs uppercase tracking-[0.35em] text-neon"
          >
            The Joe Fenix Method
          </MaskedLine>
          <h1
            data-testid="hero-title"
            className="mt-5 font-display font-black text-white leading-[0.9] tracking-tight text-6xl sm:text-7xl lg:text-8xl"
          >
            <MaskedLine delay={0.3}>NEVER</MaskedLine>
            <MaskedLine delay={0.45} className="text-neon text-glow">
              BETTER
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.65}>
            <p
              data-testid="hero-subtitle"
              className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-[#C4C4D0]"
            >
              Il manuale scientifico definitivo di ingegneria somatica ed
              endocrinologica per azzerare il cortisolo e risvegliare l'Adulto
              Biologico.
            </p>
          </MaskedLine>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-6"
          >
            <button
              onClick={() => scrollTo("#capitoli")}
              className="border border-white/20 text-white text-xs font-mono uppercase tracking-[0.25em] px-6 py-3 hover:border-neon hover:text-neon transition-colors"
            >
              Leggi il Metodo ↓
            </button>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#858599]">
              Nessuna registrazione richiesta — accesso completo
            </span>
          </motion.div>
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="relative flex justify-center"
          style={{ perspective: 1200 }}
        >
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] aspect-square rounded-full bg-neon/20 blur-[120px]" />
          <motion.div
            style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
            className="relative"
          >
            <img
              data-testid="hero-book-image"
              src={BOOK_URL}
              alt="NEVER BETTER — The Joe Fenix Method, copertina del manuale nera con accenti arancione neon"
              className="relative w-72 sm:w-80 lg:w-96 border border-white/10 shadow-[0_40px_80px_rgba(0,0,0,0.8)]"
            />
            <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-3/4 h-8 bg-black/80 blur-xl rounded-full" />
          </motion.div>
        </motion.div>
      </div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#858599]">
          Scroll
        </span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="w-px h-10 bg-gradient-to-b from-neon to-transparent"
        />
      </motion.div>
    </section>
  );
}
